import type { CSSProperties } from "react";
import type { QuizCoverStyle, QuizDifficulty, QuizThemeConfig } from "@/features/quiz/types";

/**
 * `QZ-BLD05` (`docs/23` §9.1.8): a quiz's look as students see it, the
 * author's accent colour and the layout of the quiz page.
 *
 * The quiz page, the pre-quiz screen, the player and the builder's "How
 * students see it" all read the colour and the layout through this file, so
 * the preview cannot say something the pages do not.
 *
 * The colour never reaches a page as text: it is parsed here, and only the
 * three channels of a colour that parsed are written, as CSS variables in a
 * style object. Anything else is the default.
 */

/** The brand orange, a quiz's colour when its author picked none (`docs/03` §3). */
export const QUIZ_ACCENT_DEFAULT = "#E94D1D";

/** The pre-quiz screen's and the player's ground, `bg-[#0B0B0C]` in both. */
const PLAYER_GROUND = "#0B0B0C";

/** White words on the fill: `docs/03` §12's 4.5:1 for body text. */
const TEXT_CONTRAST = 4.5;
/** A fill on the player's dark ground (the progress bar): 3:1, for a control. */
const DARK_GROUND_CONTRAST = 3;

type Rgb = [number, number, number];

/** `#RGB` or `#RRGGBB`, the server's rule, as `#RRGGBB` in capitals; `null` for anything else. */
export function parseHexColor(value: string | null | undefined): string | null {
  const digits = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((value ?? "").trim())?.[1];
  if (!digits) return null;
  const full = digits.length === 3 ? [...digits].map((d) => d + d).join("") : digits;
  return `#${full.toUpperCase()}`;
}

function toRgb(hex: string): Rgb {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mapRgb([r, g, b]: Rgb, fn: (channel: number) => number): Rgb {
  return [fn(r), fn(g), fn(b)];
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((c) => c.toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

function luminance([r, g, b]: Rgb): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2 contrast of two `#RRGGBB` colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(toRgb(a)), luminance(toRgb(b))].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * The contrast guard: the colour itself when white text on it reads at
 * 4.5:1, otherwise the first of a hundred steps towards black that does.
 * Each step scales the three channels together, so the hue stays; black is
 * the hundredth step and reads at 21:1, so it always ends.
 */
export function readableAccent(hex: string): string {
  const rgb = toRgb(hex);
  for (let step = 0; step <= 100; step += 1) {
    const candidate = toHex(mapRgb(rgb, (c) => Math.round((c * (100 - step)) / 100)));
    if (contrastRatio(candidate, "#FFFFFF") >= TEXT_CONTRAST) return candidate;
  }
  return "#000000";
}

/**
 * The same colour as a fill on the player's near-black: the colour itself at
 * 3:1 against it, otherwise the first step towards white that is, so a dark
 * accent's progress bar is still seen there.
 */
export function accentOnDark(hex: string): string {
  const rgb = toRgb(hex);
  for (let step = 0; step <= 100; step += 1) {
    const candidate = toHex(mapRgb(rgb, (c) => Math.round(c + ((255 - c) * step) / 100)));
    if (contrastRatio(candidate, PLAYER_GROUND) >= DARK_GROUND_CONTRAST) return candidate;
  }
  return "#FFFFFF";
}

export type QuizAccent = {
  /** What the author picked, parsed; the default when they picked nothing usable. */
  color: string;
  /** The fill white text sits on (the Start button). */
  fill: string;
  /** The fill on the player's dark ground (the progress bar). */
  onDark: string;
  /** `fill` is darker than `color`, which the builder says beside the preview. */
  adjusted: boolean;
};

/**
 * The accent a quiz's pages draw. The brand orange is the site's own primary
 * action, which the brand guide already holds to the 3:1 of a control
 * (`docs/03` §3 and §12), so it stays exactly as every other Progsity button;
 * any other colour is the author's and is held to 4.5:1 for the words on it.
 */
export function quizAccent(theme?: Pick<QuizThemeConfig, "primaryColor"> | null): QuizAccent {
  const color = parseHexColor(theme?.primaryColor) ?? QUIZ_ACCENT_DEFAULT;
  const fill = color === QUIZ_ACCENT_DEFAULT ? color : readableAccent(color);
  return { color, fill, onDark: accentOnDark(fill), adjusted: fill !== color };
}

export type QuizAccentStyle = CSSProperties & {
  "--theme-accent": string;
  "--theme-accent-on-dark": string;
};

/**
 * The accent as the CSS variables `Button variant="accent"` and the quiz
 * pages' accent classes read, each an `R G B` triplet for
 * `rgb(var(--theme-accent) / <alpha>)`. Set on a page's root element.
 */
export function quizAccentStyle(theme?: Pick<QuizThemeConfig, "primaryColor"> | null): QuizAccentStyle {
  const { fill, onDark } = quizAccent(theme);
  return {
    "--theme-accent": toRgb(fill).join(" "),
    "--theme-accent-on-dark": toRgb(onDark).join(" "),
  };
}

const COVER_STYLES: readonly QuizCoverStyle[] = ["banner", "card", "minimal"];

/** The quiz page's layout: one of the three, `card` (the server's default) for anything else. */
export function quizCoverLayout(theme?: Pick<QuizThemeConfig, "coverStyle"> | null): QuizCoverStyle {
  return COVER_STYLES.find((style) => style === theme?.coverStyle) ?? "card";
}

/**
 * The cover drawn when a quiz has no image, by difficulty, on `bg-neutral-900`
 * (the quiz page's banner and card, the catalogue card and the builder's
 * preview).
 */
export const QUIZ_COVER_GRADIENTS: Record<QuizDifficulty, string> = {
  easy: "from-emerald-500/20 to-teal-700/30",
  medium: "from-amber-500/20 to-orange-600/30",
  hard: "from-red-500/20 to-rose-700/30",
  mixed: "from-[#E94D1D]/20 to-purple-600/30",
};
